// Fix typescript warnings
/// <reference types="@types/google.maps" />

import { isNil } from "../..";
import { GeoPoint } from "../base/GeoPoint";
import { Locality } from "../base/Locality";
import { GoogleGeocoderAddressComponentType as AddressComponentType } from "./GoogleGeocoderAddressType";
import { GooglePlace } from "./GooglePlace";

const getGeoPoint = (geometry: google.maps.places.PlaceGeometry | undefined): GeoPoint | undefined => {
  if (!geometry) return;
  const latitude = geometry.location?.lat();
  const longitude = geometry.location?.lng();
  if (isNil(latitude) || isNil(longitude)) return;
  return { latitude, longitude };
};
/**
 * Parses `PlaceResult` and converts it into `GooglePlace`
 * https://developers.google.com/maps/documentation/geocoding/intro#GeocodingResponses
 * https://developers.google.com/maps/documentation/javascript/geocoding
 * @param {google.maps.places.PlaceResult} args.place - Place result
 * @param {string} args.address - Address input that is used for the Autocomplete prediction
 * @returns {GooglePlace|undefined} Return GooglePlace if PlaceResult is a valid Google address, otherwise, undefined.
 */
const getGooglePlace = (args: { placeResult: google.maps.places.PlaceResult }): GooglePlace | undefined => {
  const { placeResult: { address_components, geometry } } = args; // prettier-ignore

  // check if address components are present
  if (!address_components) {
    console.warn(`address_components is missing in ${args.placeResult}`);
    return;
  }

  const locality = getAddressComponent({ address_components, type: AddressComponentType.Locality });
  const suburb = locality?.long_name;

  const administrativeAreaLevel1 = getAddressComponent({
    address_components,
    type: AddressComponentType.AdministrativeAreaLevel1,
  });
  const state = administrativeAreaLevel1?.long_name;
  if (!state) {
    console.warn(`cannot find state in ${args.placeResult}`);
    return;
  }

  const countryComponent = getAddressComponent({ address_components, type: AddressComponentType.Country });
  const countryName = countryComponent?.long_name;
  if (!countryName) {
    console.warn(`cannot find country name in ${countryComponent}`);
    return;
  }

  const countryCode = countryComponent?.short_name;
  if (!countryCode) {
    console.warn(`cannot find country code in ${countryComponent}`);
    return;
  }

  const country = {
    name: countryName,
    code: countryCode,
  };

  const postcodeComponent = getAddressComponent({ address_components, type: AddressComponentType.PostalCode });
  const geoPoint = getGeoPoint(geometry);

  return {
    formattedAddress: args.placeResult.formatted_address,
    suburb,
    state,
    postcode: postcodeComponent?.long_name,
    country,
    geoPoint,
    googlePlaceId: args.placeResult.place_id,
  };
};

const getAddressComponent = (args: {
  address_components: google.maps.GeocoderAddressComponent[];
  type: AddressComponentType;
}): google.maps.GeocoderAddressComponent | undefined => {
  const { address_components, type } = args;

  return address_components.find((component) => component.types.includes(type));
};

function getLocality(args: { placeResult: google.maps.places.PlaceResult }): Locality | undefined {
  const googlePlace = getGooglePlace(args);
  if (!googlePlace) return;
  return {
    suburb: googlePlace.suburb,
    state: googlePlace.state,
    countryCode: googlePlace.country.code,
    geoPoint: googlePlace.geoPoint,
    postcode: googlePlace.postcode,
  };
}

export const fromGooglePlaceResult = {
  getGooglePlace,
  getLocality,
};
